import React from 'react'
import {
  Bullet,
  Bullets,
  Info,
  Page,
  Screenshot,
  Section,
  Step,
  Warning,
} from '../../components/DocsComponents'
import DocLink from '../../components/DocLink'

const ReviewingAndManagingPRFs = () => (
  <Page
    title="Reviewing and Managing Patient Report Forms"
    intro="Patient Report Forms (PRFs) bring together completed ePCRs, Fast Track, Welfare and Critical Care records, and uploaded paper records so they can be searched, reviewed, and audited in one place."
  >
    <Section title="Who can do this?">
      <p>
        The whole Patient Report Forms area is a governance function. The
        server only serves searches and record views to members of the{' '}
        <strong>Governance</strong> team, and you must pass a multi-factor
        authentication (MFA) check when opening the area. Crews do not need
        this access — they reach their own records through the ePCR dashboard
        and Recents.
      </p>
    </Section>

    <Section title="The dashboard">
      <p>
        Open <DocLink url="/patient-report-forms" text="Patient Report Forms" />{' '}
        to see the Care Records dashboard. Three stat tiles sit above the
        search — each one is clickable and runs the matching search:
      </p>
      <Bullets>
        <Bullet>
          <strong>In Progress</strong> — draft clinical records currently
          being edited.
        </Bullet>
        <Bullet>
          <strong>Recently Created</strong> — records created in the last 48
          hours.
        </Bullet>
        <Bullet>
          <strong>Being Shared</strong> — share codes that can be used right
          now. Clicking it opens the access log filtered to recent sharing
          activity.
        </Bullet>
      </Bullets>
      <Screenshot url="patient-records/prf-dashboard.png">
        The Care Records dashboard with its clickable stat tiles above the
        record search.
      </Screenshot>
      <p>
        Below the tiles, two tabs switch the page between{' '}
        <strong>Records</strong> (search and results) and{' '}
        <strong>Access log</strong> — who has viewed, downloaded, shared or
        updated patient care records. See{' '}
        <DocLink docsPage url="/clinical-records/sharing-and-access-logs" text="Sharing and Access Logs" />{' '}
        for how the log entries work.
      </p>
    </Section>

    <Section title="Searching for records">
      <Bullets>
        <Bullet>
          The main search bar takes an exact ePCR number, a date range (with
          one-click presets for Today, Past 7 Days, Past 30 Days, This Month,
          and All Dates), and a linked event.
        </Bullet>
        <Bullet>
          <strong>Advanced</strong> filters add record status (Draft or
          Submitted), record type — ePCR, Fast Track, Welfare, Critical Care,
          and Paper — patient name and date of birth, the completing
          clinician, and a Presenting Complaint filter that matches the
          complaints recorded in the record&apos;s history.
        </Bullet>
        <Bullet>
          Governance flags narrow results to records with safeguarding
          concerns (<strong>New</strong>, <strong>Completed</strong>, or{' '}
          <strong>Any</strong>) or with a <strong>H&amp;S Concern</strong> —
          the same views the dashboard chips on the home page link to.
        </Bullet>
        <Bullet>
          The results grid has optional columns you can switch on from the
          column picker, including <strong>Occurred at Event</strong> and{' '}
          <strong>H&amp;S Reviewed</strong> — useful when working through
          post-event analysis or a health and safety review.
        </Bullet>
      </Bullets>
    </Section>

    <Section title="Opening a record">
      <Step number={1} title="Give a reason for access">
        Before a report opens you must record a reason for accessing it. The
        reason is written to the record&apos;s access log, and the record will
        not load without it.
      </Step>
      <Step number={2} title="Review the report">
        The report view shows every module of the record — demographics,
        incident, safeguarding, health and safety, history, assessment,
        vitals, treatment, plan, attachments — plus Clinical Advice Line
        notes and share codes. The header shows the record status (Draft or
        Submitted) and, from the record&apos;s key dates, who created it, its
        full ownership history (every transfer with who moved it, when, and
        why), and who currently holds it.
      </Step>
      <Step number={3} title="Use the header menu">
        The header menu offers <strong>Download PDF</strong> and{' '}
        <strong>Anonymised PDF</strong> (both tier 2 subscription features —
        every PDF download is written to the access log),{' '}
        <strong>Create Audit</strong> to raise a record audit by hand,{' '}
        <strong>Access log</strong> to see that record&apos;s access history,
        and <strong>Reassign Record</strong> and <strong>Delete Record</strong>{' '}
        (below).
      </Step>
    </Section>

    <Section title="Record audits">
      <p>
        Submitted records are checked against your organisation&apos;s audit
        criteria and flagged for a quality audit on{' '}
        <DocLink url="/patient-records/audits" text="Record Audits" />. An
        audit can also be raised by hand — from <strong>Create Audit</strong>{' '}
        in the report header menu, or from the Record Audits page itself. See{' '}
        <DocLink docsPage url="/clinical-records/record-audits" text="Record Audits" />{' '}
        for the criteria and the full workflow.
      </p>
    </Section>

    <Section title="Reassigning and transferring ownership">
      <p>
        A record can be handed between users in two ways: the owning clinician
        can transfer ownership to a colleague from the Sharing module inside
        the ePCR, or a Governance user can use <strong>Reassign Record</strong>{' '}
        in the report view. A manager reassignment requires a reason, is
        recorded in the audit trail with a snapshot of the record, and
        returns the record to Draft with the new owner.
      </p>
    </Section>

    <Section title="Deleting records">
      <p>
        <strong>Delete Record</strong> in the report header menu offers two
        options. A soft delete hides the record from normal views but keeps
        it securely stored (retrievable via written request to AmbuNet); a
        hard delete removes it permanently. Both require a written reason and
        a compliance confirmation, and both are written to the audit trail.
      </p>
      <Warning title="Hard delete is permanent">
        Only use a hard delete when you are certain the record must be
        permanently removed — for example a lawful erasure request. Prefer a
        soft delete when in doubt.
      </Warning>
    </Section>

    <Section title="Paper PRFs and legacy records">
      <Bullets>
        <Bullet>
          Upload a scanned or photographed paper record from{' '}
          <DocLink
            url="/patient-report-forms/upload-paper-prf"
            text="Upload Paper PRF"
          />
          . Uploading and editing paper PRFs is limited to the Governance
          team. Paper PRFs are viewed separately from ePCRs but appear
          alongside them in search results.
        </Bullet>
        <Bullet>
          Uninjured Competitor Records (UCR) are retired — the server rejects
          new ones and directs crews to Fast Track ePCRs instead.
        </Bullet>
      </Bullets>
      <Info title="Access logs">
        Every time a report is opened, updated, or downloaded, an access log
        entry is recorded. See{' '}
        <DocLink docsPage url="/clinical-records/sharing-and-access-logs" text="Sharing and Access Logs" />{' '}
        for how those logs and share codes work.
      </Info>
    </Section>
  </Page>
)

export default ReviewingAndManagingPRFs
